import React, { useState, useEffect, useMemo } from 'react';
import { Table, Badge, Button, Row, Col, Card, Spinner, Form } from 'react-bootstrap';
import { FaFilePdf, FaRobot, FaSearch, FaSync, FaChartLine, FaExclamationTriangle, FaCheckCircle, FaAward } from 'react-icons/fa';
import moment from 'moment';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { getAttendance } from '../../services/firebase/attendanceService';
import { useAuth } from '../../context/AuthContext';
import EmployeeSelector from './calendar/EmployeeSelector';
import { isDeptVisibleToUser } from '../../utils/departmentVisibility';
import { getEmployees } from '../../services/firebase/employeeService';
import _ from 'lodash';

/**
 * Helper to normalize employee IDs across the system (e.g. "05", "5", 5 -> "5")
 */
const normalizeId = (id) => {
    if (id === null || id === undefined) return '';
    const s = String(id).trim();
    if (/^\d+$/.test(s)) return String(parseInt(s, 10));
    return s.toLowerCase();
};

/**
 * DSADS – INTELLIGENT HR ANALYTICS ENGINE (Version 1.1)
 * Optimized Scoring & Professional Reporting
 */
const DSADSAnalytics = ({ month, year }) => {
    const { user } = useAuth();
    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(false);

    const isPrivilegedUser = user?.role === 'admin' || user?.role === 'hr' || user?.role === 'manager';
    const canSelectEmployees = isPrivilegedUser || user?.role === 'dept_head' || user?.role === 'production_head';
    const isEmployee = user?.role === 'employee';

    // Filters
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    // For regular employees, lock them to their own ID
    const [selectedEmployeeIds, setSelectedEmployeeIds] = useState(
        isEmployee ? [user?.employeeCode || user?.uid || user?.id] : []
    );

    // Update selectedEmployeeIds if user changes and is employee
    useEffect(() => {
        if (isEmployee) {
            setSelectedEmployeeIds([user?.employeeCode || user?.uid || user?.id]);
        }
    }, [user, isEmployee]);

    useEffect(() => {
        const fetchAllAttendance = async () => {
            setLoading(true);
            try {
                // Fetch employees for department and canonical name mapping
                const empRes = await getEmployees();
                const allEmps = empRes?.data || [];
                const empIdToDeptMap = new Map();
                const uidToEmpCodeMap = new Map();
                const empIdToCanonicalNameMap = new Map();

                allEmps.forEach(emp => {
                    const id1 = normalizeId(emp.employeeId);
                    const id2 = normalizeId(emp.id || emp.uid);
                    const cleanName = `${emp.firstName || ''} ${emp.lastName || ''}`.trim() || emp.fullName || emp.displayName || 'Unknown';

                    if (id1) {
                        empIdToDeptMap.set(id1, emp.department);
                        empIdToCanonicalNameMap.set(id1, cleanName);
                    }
                    if (id2) {
                        empIdToDeptMap.set(id2, emp.department);
                        empIdToCanonicalNameMap.set(id2, cleanName);
                    }
                    
                    // Map UID to Employee Code so that mismatched Extra Work records correctly map to the user
                    if (id2 && id1 && id2 !== id1) {
                        uidToEmpCodeMap.set(id2, id1);
                    }
                });

                const res = await getAttendance({ month, year });
                let attendanceRecords = res.data || [];
                
                // Normalize employeeId and canonical employee name in records
                attendanceRecords = attendanceRecords.map(r => {
                    let currentNormId = normalizeId(r.empCode || r.employeeId || r.uid || '');
                    if (uidToEmpCodeMap.has(currentNormId)) {
                        currentNormId = uidToEmpCodeMap.get(currentNormId);
                    }
                    // Determine canonical employee name
                    let canonicalName = empIdToCanonicalNameMap.get(currentNormId);
                    if (!canonicalName) {
                        if (r.employeeName && r.employeeName.toLowerCase().includes('yasin')) {
                            canonicalName = 'Zarin Jamadar';
                        } else {
                            canonicalName = r.employeeName;
                        }
                    }
                    return {
                        ...r,
                        employeeId: currentNormId,
                        empCode: currentNormId,
                        normalizedEmployeeId: currentNormId,
                        canonicalEmployeeName: canonicalName || r.employeeName || 'Unknown'
                    };
                });
                const isDeptOrProdHead = user?.role === 'dept_head' || user?.role === 'production_head';
                if (isDeptOrProdHead) {
                    attendanceRecords = attendanceRecords.filter(r => {
                        const normId = r.normalizedEmployeeId || normalizeId(r.empCode || r.employeeId || r.uid || '');
                        const actualDept = empIdToDeptMap.get(normId) || r.department || r.dept || '';
                        return isDeptVisibleToUser(actualDept, actualDept, user);
                    });
                }
                setRecords(attendanceRecords);
            } catch (error) {
                console.error('Error fetching DSADS data:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchAllAttendance();
    }, [month, year]);

    // --- ROBUST TIME PARSER ---
    const parseTimeToMins = (tStr) => {
        if (!tStr || typeof tStr !== 'string' || tStr === '-') return null;
        const match = tStr.match(/(\d{1,2})[:.](\d{1,2})/);
        if (!match) return null;
        let h = parseInt(match[1], 10);
        const m = parseInt(match[2], 10);
        if (tStr.toLowerCase().includes('pm') && h < 12) h += 12;
        if (tStr.toLowerCase().includes('am') && h === 12) h = 0;
        return h * 60 + m;
    };

    // Official Daily Scoring (10 Marks Rule)
    const getDailyScore = (inTimeStr, outTimeStr, status) => {
        if (!status) return { score: 0, max: 0, factors: [], isOff: false };
        const s = status.toLowerCase();

        // Rule 1: Weekly Off or Official Off is exempt
        if (s.includes('weekly') || s.includes('off') || s === 'w-off') {
            return { score: 0, max: 0, factors: ['Weekly Off'], isOff: true };
        }

        // Rule 2: Exempt status (Special cases where we don't want to score)
        if (s.includes('exempt')) {
            return { score: 0, max: 0, factors: ['Exempt'], isOff: true };
        }

        const inMins = parseTimeToMins(inTimeStr);
        const outMins = parseTimeToMins(outTimeStr);
        
        // If no punch at all AND status is Absent/Leave, return 0/10
        if (inMins === null && (s.includes('absent') || s.includes('leave'))) {
            return { score: 0, max: 10, factors: ['Absent/Leave'], isOff: false };
        }

        let score = 0;
        const factors = [];
        
        // Even if status says "Absent", if there is an inTime, we evaluate the score
        if (inMins !== null) {
            // 1. On Time (+1) - Arriving by 09:35
            if (inMins <= 9 * 60 + 35) {
                score += 1;
                factors.push('On Time (+1)');
            }
            // 2. Late nahi (+3) - Arriving by 09:35
            if (inMins <= 9 * 60 + 35) {
                score += 3;
                factors.push('Not Late (+3)');
            }
            // 3. Super Late nahi (+1) - Arriving by 09:40
            if (inMins <= 9 * 60 + 40) {
                score += 1;
                factors.push('Not Super Late (+1)');
            }
            // 4. Prarthana (+2) - Must punch by 09:30 AM sharp
            if (inMins <= 9 * 60 + 30) {
                score += 2;
                factors.push('Prarthana OK (+2)');
            }
        }

        // 5. Punch Accuracy (+3) - Full Punch Presence (Both In & Out)
        if (inMins !== null && outMins !== null) {
            score += 3;
            factors.push('Punch Status OK (+3)');
        }

        return { score, max: 10, factors, isOff: false };
    };

    // Assessment Categories (100 Point Scaling)
    const getDetailedAssessment = (score) => {
        let assessment = {
            category: 'Critical',
            legacyCategory: 'Critical',
            color: 'critical',
            actionPlan: 'Replace / strict decision (Immediate review)',
            observations: ['Very low attendance', 'Frequent absence / no punch'],
            positives: ['Few consistent days', 'Some effort visible']
        };

        if (score >= 80) {
            assessment = {
                category: 'Performer',
                legacyCategory: 'Stable Performer',
                color: 'performer',
                actionPlan: 'Reward / grow (Incentive: ₹1000 - ₹5000)',
                observations: ['Good discipline record', 'Consistent timing'],
                positives: ['Strong attendance', 'Disciplined team player']
            };
        } else if (score >= 70) {
            assessment = {
                category: 'Inconsistent',
                legacyCategory: 'Moderate',
                color: 'inconsistent',
                actionPlan: 'Improve (15 days monitoring)',
                observations: ['Inconsistent reporting pattern', 'Occasional late entries'],
                positives: ['Good effort visible', 'Responsive to feedback']
            };
        } else if (score >= 55) {
            assessment = {
                category: 'Inconsistent',
                legacyCategory: 'Needs Improvement',
                color: 'inconsistent',
                actionPlan: 'Improve (15 days monitoring)',
                observations: ['Late reporting pattern', 'Multiple late cases'],
                positives: ['Strong attendance record', 'Decent OT effort']
            };
        } else if (score >= 40) {
            assessment = {
                category: 'Risk',
                legacyCategory: 'Critical Attention',
                color: 'risk',
                actionPlan: 'Warning + tracking (Strict 15-day period)',
                observations: ['Very high absentee rate', 'Frequent super late cases'],
                positives: ['Some disciplined stretches', 'High OT contribution']
            };
        }

        return assessment;
    };

    const analyticsData = useMemo(() => {
        if (!records.length) return [];

        const start = startDate ? moment(startDate) : moment([year, month - 1]).startOf('month');
        const end = endDate ? moment(endDate) : moment([year, month - 1]).endOf('month');

        const normSelectedIds = selectedEmployeeIds.map(id => normalizeId(id));

        const filteredRecords = records.filter(r => {
            const rNormId = r.normalizedEmployeeId || normalizeId(r.empCode || r.employeeId || r.uid || '');
            const isEmp = !normSelectedIds.length || normSelectedIds.includes(rNormId);
            if (!isEmp) return false;
            const dateStr = r.dateString || (r.date?.toDate ? moment(r.date.toDate()).format('YYYY-MM-DD') : '');
            return (!startDate || dateStr >= startDate) && (!endDate || dateStr <= endDate);
        });

        const groups = _.groupBy(filteredRecords, r => r.normalizedEmployeeId || normalizeId(r.empCode || r.employeeId || r.uid));

        return Object.entries(groups).map(([empId, empRecords]) => {
            let presentCount = 0, lateCount = 0, superLateCount = 0, prarthanaCount = 0;
            let noPunchCount = 0, otMins = 0, totalDailyMarks = 0, totalMaxMarks = 0, inTimes = [];
            let totalWorkDays = 0;

            let cur = start.clone();
            const obsSet = new Set();
            const posSet = new Set();

            while (cur.isSameOrBefore(end)) {
                const isWeeklyOff = cur.day() === 2;
                const dStr = cur.format('YYYY-MM-DD');
                const dayR = empRecords.find(r => moment(r.dateString || r.date?.toDate?.()).format('YYYY-MM-DD') === dStr);

                if (!isWeeklyOff) {
                    totalWorkDays++;
                    const scoreObj = getDailyScore(dayR?.inTime, dayR?.outTime, dayR?.status || (dayR ? 'Present' : 'Absent'));
                    totalDailyMarks += scoreObj.score;
                    totalMaxMarks += scoreObj.max;

                    const inMins = parseTimeToMins(dayR?.inTime);
                    if (inMins !== null) {
                        presentCount++;
                        inTimes.push(inMins);

                        if (inMins > 9 * 60 + 35) {
                            lateCount++;
                            obsSet.add("Late Arrival");
                        }
                        if (inMins > 9 * 60 + 40) {
                            superLateCount++;
                            obsSet.add("Super Late");
                        }
                        if (inMins <= 9 * 60 + 33) {
                            prarthanaCount++;
                            posSet.add("Punctual (Prayer)");
                        }

                        const outMins = parseTimeToMins(dayR?.outTime);
                        if (outMins !== null) {
                            if (outMins > 18 * 60 + 30) otMins += (outMins - (18 * 60 + 30));
                        } else {
                            noPunchCount++;
                        }
                    } else {
                        if (dayR && dayR.status === 'Absent') {
                            obsSet.add("Absenteeism");
                        } else if (dayR && (dayR.status === 'Leave' || dayR.status === 'Half-day')) {
                            obsSet.add("Took Leave");
                        } else if (!dayR) {
                            obsSet.add("Missing Status");
                        }
                    }
                }
                cur.add(1, 'days');
            }

            if (presentCount === totalWorkDays && totalWorkDays > 0) posSet.add("Perfect Attendance");

            const sdsScore = totalMaxMarks > 0 ? (totalDailyMarks / totalMaxMarks) * 100 : 0;
            const assessment = getDetailedAssessment(Math.round(sdsScore));

            const avgInMins = inTimes.length ? _.mean(inTimes) : 0;
            // Realistic Average: Exclude top 15% and bottom 15% outliers to find the typical performace pattern.
            // This prevents single exceptional early or late entries from skewing the true daily average.
            let sortedIn = [...inTimes].sort((a, b) => a - b);
            if (sortedIn.length >= 5) {
                const trimCount = Math.max(1, Math.floor(sortedIn.length * 0.15));
                sortedIn = sortedIn.slice(trimCount, -trimCount);
            } else if (sortedIn.length >= 3) {
                sortedIn = sortedIn.slice(1, -1);
            }
            const realAvgMins = sortedIn.length ? _.mean(sortedIn) : avgInMins;

            const formatTime = (mins) => {
                if (!mins || isNaN(mins)) return '00:00';
                let h = Math.floor(mins / 60);
                let m = Math.round(mins % 60);
                if (m === 60) {
                    h += 1;
                    m = 0;
                }
                return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
            };

            let empDisplayName = empRecords.find(r => r.canonicalEmployeeName)?.canonicalEmployeeName;
            if (!empDisplayName) {
                empDisplayName = empRecords.find(r => r.employeeName && !r.employeeName.toLowerCase().includes('yasin'))?.employeeName
                    || empRecords[0]?.employeeName
                    || 'Unknown';
            }
            if (empDisplayName.toLowerCase().includes('yasin')) {
                empDisplayName = 'Zarin Jamadar';
            }

            return {
                id: empId,
                name: empDisplayName,
                monthLabel: start.format('MMM YYYY'),
                totalWorkDays,
                presentDays: presentCount,
                leaveDays: Math.max(0, totalWorkDays - presentCount),
                avgInTime: formatTime(avgInMins),
                realisticAvg: formatTime(realAvgMins),
                superLateDays: superLateCount,
                prarthanaPct: presentCount > 0 ? ((prarthanaCount / presentCount) * 100).toFixed(1) : '0.0',
                avgOTTime: presentCount > 0 ? formatTime(otMins / presentCount) : '00:00',
                sdsScore: Math.round(sdsScore),
                earnedPoints: totalDailyMarks,
                maxPoints: totalMaxMarks,
                penaltyAmount: (noPunchCount * 200) + (superLateCount * 100),
                assessment: assessment,
                finalAssessment: assessment.legacyCategory,
                observations: assessment.observations || [],
                positives: assessment.positives || []
            };
        }).sort((a, b) => b.sdsScore - a.sdsScore);
    }, [records, month, year, startDate, endDate, selectedEmployeeIds]);

    const handleDownloadPDF = () => {
        const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
        const monthHeader = moment([year, month - 1]).format('MMMM YYYY');

        // If employee, they always download their own individual report
        const isIndividual = isEmployee || selectedEmployeeIds.length === 1;
        const targetEmpId = isEmployee ? (user?.employeeCode || user?.uid || user?.id) : (isIndividual ? selectedEmployeeIds[0] : null);
        const targetEmpNorm = normalizeId(targetEmpId);

        const reportData = isIndividual
            ? analyticsData.filter(d => normalizeId(d.id) === targetEmpNorm)
            : analyticsData;

        if (reportData.length === 0) return;

        doc.setFontSize(16).setTextColor(7, 85, 85).text(`DSADS PERFORMANCE REPORT: ${monthHeader}`, 148.5, 15, { align: 'center' });

        const clean = (s) => (s || '').toString()
            .replace(/[🟢✅🟡⚠️🟠⚠️🔴❌]/g, '')
            .replace(/\[GO\]|\[WATCH\]|\[WARN\]|\[EXIT\]/g, '')
            .replace(/→|->/g, '')
            .replace(/₹/g, 'Rs.')
            .trim();

        if (!isIndividual) {
            const columns = [
                "Sr", "Employee Name", "Month", "Working", "Present", "Leave", "Avg In", "Real Avg", "S.Late",
                "Pray%", "OT", "Raw Score", "Score%", "Assessment", "Final", "Penalty", "Observations", "Positives", "Action Plan"
            ];
            const rows = reportData.map((d, i) => [
                i + 1, d.name, d.monthLabel, d.totalWorkDays, d.presentDays, d.leaveDays, d.avgInTime, d.realisticAvg, d.superLateDays,
                `${d.prarthanaPct}%`, d.avgOTTime, `${d.earnedPoints}/${d.maxPoints}`, `${d.sdsScore}%`,
                d.assessment.category, d.finalAssessment, `Rs.${d.penaltyAmount}`,
                d.observations?.join(", ") || "-", d.positives?.join(", ") || "-", clean(d.assessment.actionPlan)
            ]);

            autoTable(doc, {
                head: [columns],
                body: rows,
                startY: 25,
                theme: 'grid',
                styles: { fontSize: 4.2, cellPadding: 0.8, halign: 'center', overflow: 'linebreak' },
                headStyles: { fillColor: [0, 0, 0], fontSize: 4.5, textColor: 255 },
                columnStyles: {
                    1: { halign: 'left', cellWidth: 25 },
                    16: { halign: 'left', cellWidth: 25 },
                    17: { halign: 'left', cellWidth: 25 },
                    18: { halign: 'left', cellWidth: 35 }
                },
                didParseCell: (data) => {
                    if (data.section === 'body') {
                        // Color Assessment Column
                        if (data.column.index === 13) {
                            const val = data.cell.raw;
                            if (val === 'Performer') data.cell.styles.textColor = [25, 135, 84];
                            if (val === 'Inconsistent') data.cell.styles.textColor = [255, 193, 7];
                            if (val === 'Risk') data.cell.styles.textColor = [253, 126, 20];
                            if (val === 'Critical') data.cell.styles.textColor = [220, 53, 69];
                            data.cell.styles.fontStyle = 'bold';
                        }
                        // Color Penalty Column
                        if (data.column.index === 15) {
                            data.cell.styles.textColor = [220, 53, 69];
                            data.cell.styles.fontStyle = 'bold';
                        }
                        // Color Score Column
                        if (data.column.index === 12) {
                            data.cell.styles.textColor = [13, 110, 253];
                            data.cell.styles.fontStyle = 'bold';
                        }
                    }
                }
            });
            doc.save(`DSADS_Team_Report_${monthHeader}.pdf`);
        } else {
            const emp = reportData[0];
            const columnsH = [
                "Sr", "Employee Name", "Month", "Working", "Present", "Leave", "Avg In", "Real Avg", "S.Late",
                "Pray%", "OT", "Raw Score", "Score%", "Assessment", "Final", "Penalty", "Observations", "Positives", "Action Plan"
            ];
            const rowsH = [[
                1, emp.name, emp.monthLabel, emp.totalWorkDays, emp.presentDays, emp.leaveDays, emp.avgInTime, emp.realisticAvg, emp.superLateDays,
                `${emp.prarthanaPct}%`, emp.avgOTTime, `${emp.earnedPoints}/${emp.maxPoints}`, `${emp.sdsScore}%`,
                emp.assessment.category, emp.finalAssessment, `Rs.${emp.penaltyAmount}`,
                emp.observations?.join(", ") || "-", emp.positives?.join(", ") || "-", clean(emp.assessment.actionPlan)
            ]];

            autoTable(doc, {
                head: [columnsH],
                body: rowsH,
                startY: 25,
                theme: 'grid',
                styles: { fontSize: 4.2, cellPadding: 0.8, halign: 'center', overflow: 'linebreak' },
                headStyles: { fillColor: [0, 0, 0], fontSize: 4.5, textColor: 255 },
                columnStyles: {
                    1: { halign: 'left', cellWidth: 25 },
                    16: { halign: 'left', cellWidth: 25 },
                    17: { halign: 'left', cellWidth: 25 },
                    18: { halign: 'left', cellWidth: 35 }
                },
                didParseCell: (data) => {
                    if (data.section === 'body') {
                        if (data.column.index === 13) {
                            const val = data.cell.raw;
                            if (val === 'Performer') data.cell.styles.textColor = [25, 135, 84];
                            if (val === 'Inconsistent') data.cell.styles.textColor = [255, 193, 7];
                            if (val === 'Risk') data.cell.styles.textColor = [253, 126, 20];
                            if (val === 'Critical') data.cell.styles.textColor = [220, 53, 69];
                            data.cell.styles.fontStyle = 'bold';
                        }
                        if (data.column.index === 15) {
                            data.cell.styles.textColor = [220, 53, 69];
                            data.cell.styles.fontStyle = 'bold';
                        }
                        if (data.column.index === 12) {
                            data.cell.styles.textColor = [13, 110, 253];
                            data.cell.styles.fontStyle = 'bold';
                        }
                    }
                }
            });

            const finalY = doc.lastAutoTable.finalY || 40;
            doc.setFontSize(14).setTextColor(7, 85, 85).text(`Daily Discipline Audit Log`, 148.5, finalY + 15, { align: 'center' });

            const start = startDate ? moment(startDate) : moment([year, month - 1]).startOf('month');
            const end = endDate ? moment(endDate) : moment([year, month - 1]).endOf('month');
            const empDays = records.filter(r => {
                const rNorm = r.normalizedEmployeeId || normalizeId(r.empCode || r.employeeId || r.uid);
                return rNorm === targetEmpNorm;
            });
            const dailyRows = [];
            let cur = start.clone();
            while (cur.isSameOrBefore(end)) {
                const isOff = cur.day() === 2;
                const dStr = cur.format('YYYY-MM-DD');
                const dayR = empDays.find(rd => moment(rd.dateString || rd.date?.toDate?.()).format('YYYY-MM-DD') === dStr);
                const score = getDailyScore(dayR?.inTime, dayR?.outTime, isOff ? 'Weekly Off' : (dayR?.status || 'Absent'));

                let pdfStatus = 'ABSENT';
                if (isOff) pdfStatus = 'WEEKLY OFF';
                else if (dayR && dayR.inTime && dayR.inTime !== '-') {
                    const mins = parseTimeToMins(dayR.inTime);
                    if (mins !== null) {
                        if (mins <= 9 * 60 + 30) pdfStatus = 'EXCELLENT';
                        else if (mins <= 9 * 60 + 35) pdfStatus = 'ON TIME';
                        else if (mins <= 9 * 60 + 40) pdfStatus = 'LATE';
                        else pdfStatus = 'SUPER LATE';
                    }
                }

                dailyRows.push([cur.format('DD MMM (ddd)'), dayR?.inTime || '-', dayR?.outTime || '-', pdfStatus, score.isOff ? '-' : `${score.score}/10`]);
                cur.add(1, 'days');
            }

            autoTable(doc, {
                head: [["Date", "In Time", "Out Time", "Status", "Daily Score"]],
                body: dailyRows,
                startY: finalY + 20,
                theme: 'grid',
                headStyles: { fillColor: [7, 85, 85] },
                styles: { fontSize: 8, cellPadding: 1.5 },
                showFoot: 'lastPage',
                foot: [[
                    { content: 'TOTALS / AVERAGES:', styles: { halign: 'right', fontStyle: 'bold' } },
                    { content: `Avg: ${emp.avgInTime}\n(Real: ${emp.realisticAvg})`, styles: { halign: 'center', fontSize: 7 } },
                    { content: `Avg OT: ${emp.avgOTTime}`, styles: { halign: 'center', fontSize: 7 } },
                    { content: '', styles: { halign: 'center' } },
                    { content: `${emp.earnedPoints}/${emp.maxPoints} (${emp.sdsScore}%)`, styles: { halign: 'center', fontStyle: 'bold' } }
                ]]
            });
            doc.save(`DSADS_Individual_Audit_${emp.name}.pdf`);
        }
    };

    const handleDownloadIndividualLog = (empId) => {
        const empIdNorm = normalizeId(empId);
        const emp = analyticsData.find(d => normalizeId(d.id) === empIdNorm);
        if (!emp) return;

        const doc = new jsPDF('p', 'mm', 'a4');
        const start = startDate ? moment(startDate) : moment([year, month - 1]).startOf('month');
        const end = endDate ? moment(endDate) : moment([year, month - 1]).endOf('month');

        doc.setFontSize(16).setTextColor(7, 85, 85).text(`DSADS AUDIT LOG: ${emp.name}`, 105, 15, { align: 'center' });
        doc.setFontSize(10).setTextColor(100).text(`Reporting Period: ${emp.monthLabel}`, 105, 22, { align: 'center' });

        const rows = [];
        let cur = start.clone();
        while (cur.isSameOrBefore(end)) {
            const dStr = cur.format('YYYY-MM-DD');
            const dayR = records.find(r => {
                const rNorm = r.normalizedEmployeeId || normalizeId(r.empCode || r.employeeId || r.uid);
                return rNorm === empIdNorm && moment(r.dateString || r.date?.toDate?.()).format('YYYY-MM-DD') === dStr;
            });
            const isOff = cur.day() === 2;
            const score = getDailyScore(dayR?.inTime, dayR?.outTime, isOff ? 'Weekly Off' : (dayR?.status || 'Absent'));

            // Detailed PDF Status
            let pdfStatus = 'ABSENT';
            if (isOff) pdfStatus = 'WEEKLY OFF';
            else if (dayR && dayR.inTime && dayR.inTime !== '-') {
                const mins = parseTimeToMins(dayR.inTime);
                if (mins !== null) {
                    if (mins <= 9 * 60 + 30) pdfStatus = 'EXCELLENT';
                    else if (mins <= 9 * 60 + 35) pdfStatus = 'ON TIME';
                    else if (mins <= 9 * 60 + 40) pdfStatus = 'LATE';
                    else pdfStatus = 'SUPER LATE';
                }
            }

            rows.push([
                cur.format('DD MMM (ddd)'),
                dayR?.inTime || '-',
                dayR?.outTime || '-',
                pdfStatus,
                score.isOff ? '-' : `${score.score}/10`
            ]);
            cur.add(1, 'days');
        }

        autoTable(doc, {
            head: [["Date", "In Time", "Out Time", "Status", "Daily Score"]],
            body: rows,
            startY: 30,
            theme: 'grid',
            headStyles: { fillColor: [7, 85, 85] },
            showFoot: 'lastPage',
            foot: [[
                { content: 'TOTALS / AVERAGES:', styles: { halign: 'right', fontStyle: 'bold' } },
                { content: `Avg: ${emp.avgInTime}\n(Real: ${emp.realisticAvg})`, styles: { halign: 'center', fontSize: 7 } },
                { content: `Avg OT: ${emp.avgOTTime}`, styles: { halign: 'center', fontSize: 7 } },
                { content: '', styles: { halign: 'center' } },
                { content: `${emp.earnedPoints}/${emp.maxPoints} (${emp.sdsScore}%)`, styles: { halign: 'center', fontStyle: 'bold' } }
            ]]
        });

        doc.save(`Audit_${emp.name}.pdf`);
    };

    if (loading) return <div className="text-center p-5"><Spinner animation="border" variant="primary" /></div>;

    return (
        <div className="dsads-analytics-main p-3">
            <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-3 mb-4 bg-white p-3 rounded-4 shadow-sm border-start border-5 border-primary">
                <div>
                    <h5 className="fw-bold mb-0"><FaRobot className="text-primary me-2" /> DSADS Intelligence Audit</h5>
                    <small className="text-muted">{isEmployee ? 'Personal Performance Monitoring' : 'Performance Monitoring Engine v1.1'}</small>
                </div>
                <Button variant="danger" size="sm" className="rounded-pill px-4 w-100 w-sm-auto" onClick={handleDownloadPDF}>
                    <FaFilePdf className="me-2" /> {isEmployee ? 'Download My Report' : 'Download Full Report'}
                </Button>
            </div>

            {(canSelectEmployees || isEmployee) && (
                <Row className="g-3 mb-4">
                    {[
                        { label: 'Performer', color: '#198754', count: analyticsData.filter(d => d.assessment.color === 'performer').length, icon: <FaAward /> },
                        { label: 'Inconsistent', color: '#ffc107', count: analyticsData.filter(d => d.assessment.color === 'inconsistent').length, icon: <FaCheckCircle /> },
                        { label: 'Risk', color: '#fd7e14', count: analyticsData.filter(d => d.assessment.color === 'risk').length, icon: <FaExclamationTriangle /> },
                        { label: 'Critical', color: '#dc3545', count: analyticsData.filter(d => d.assessment.color === 'critical').length, icon: <FaExclamationTriangle /> }
                    ].map((stat, idx) => (
                        <Col key={idx} xs={6} md={3}>
                            <Card className="border-0 shadow-sm rounded-4 h-100">
                                <Card.Body className="p-3 d-flex align-items-center justify-content-between">
                                    <div>
                                        <div className="text-uppercase text-muted fw-bold mb-1" style={{ fontSize: '0.65rem' }}>{stat.label}</div>
                                        <h2 className="fw-bold mb-0" style={{ color: stat.color }}>{stat.count}</h2>
                                    </div>
                                    <div className="p-3 rounded-3" style={{ backgroundColor: `${stat.color}15`, color: stat.color }}>
                                        {stat.icon}
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    ))}
                </Row>
            )}

            <Card className="border-0 shadow-sm rounded-4 mb-4" style={{ overflow: 'visible', zIndex: 100 }}>
                <Card.Body style={{ overflow: 'visible' }}>
                    <Row className="g-3">
                        {canSelectEmployees && (
                            <Col xs={12} lg={4}>
                                <EmployeeSelector
                                    selectedEmployeeIds={selectedEmployeeIds}
                                    onEmployeeChange={setSelectedEmployeeIds}
                                    month={month}
                                    year={year}
                                />
                            </Col>
                        )}
                        <Col xs={6} sm={6} lg={3}><Form.Control type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="bg-light border-0 py-2 rounded-3" /></Col>
                        <Col xs={6} sm={6} lg={3}><Form.Control type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="bg-light border-0 py-2 rounded-3" /></Col>
                        <Col xs={12} sm={12} lg={2}><Button variant="light" className="w-100 border-0 bg-light py-2 fw-bold text-muted rounded-3" onClick={() => { setStartDate(''); setEndDate(''); setSelectedEmployeeIds([]); }}><FaSync /> Reset</Button></Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Main Detailed Audit Table */}
            <Card className="border-0 shadow-sm rounded-4 mb-4">
                <div
                    className="table-responsive"
                    style={{
                        overflowX: 'auto',
                        WebkitOverflowScrolling: 'touch',
                        paddingBottom: '10px'
                    }}
                >
                    <Table hover className="align-middle mb-0 text-center" style={{ minWidth: '1800px' }}>
                        <thead className="bg-dark text-white text-uppercase" style={{ fontSize: '0.7rem' }}>
                            <tr>
                                <th className="py-3">Sr. No.</th>
                                <th>Employee Name</th>
                                <th>Month</th>
                                <th>Working Days</th>
                                <th>Present Days</th>
                                <th>Leave Days</th>
                                <th>Avg In-Time</th>
                                <th>Realistic Avg</th>
                                <th>Super Late</th>
                                <th>Prarthana %</th>
                                <th>Avg OT</th>
                                <th>Raw Score</th>
                                <th>Score %</th>
                                <th>Assessment</th>
                                <th>Final Assessment</th>
                                <th className="text-danger">Penalty</th>
                                <th>Key Observations</th>
                                <th>Positives</th>
                                <th>Action Plan</th>
                            </tr>
                        </thead>
                        <tbody style={{ fontSize: '0.8rem' }}>
                            {analyticsData.map((d, i) => (
                                <tr key={d.id} className="border-bottom clickable-row" onClick={() => setSelectedEmployeeIds([d.id])}>
                                    <td className="fw-bold text-muted">{i + 1}</td>
                                    <td className="fw-bold text-dark text-start">{d.name}</td>
                                    <td className="text-muted small">{d.monthLabel}</td>
                                    <td className="fw-bold">{d.totalWorkDays}</td>
                                    <td className="text-success fw-bold">{d.presentDays}</td>
                                    <td className="text-danger fw-bold">{d.leaveDays}</td>
                                    <td>{d.avgInTime}</td>
                                    <td className="text-primary fw-bold">{d.realisticAvg}</td>
                                    <td className="text-warning fw-bold">{d.superLateDays}</td>
                                    <td><Badge bg="primary" className="rounded-pill px-3">{d.prarthanaPct}%</Badge></td>
                                    <td>{d.avgOTTime}</td>
                                    <td className="fw-bold text-dark">{d.earnedPoints}/{d.maxPoints}</td>
                                    <td className="text-center"><Badge bg="primary" className="rounded-pill px-3">{d.sdsScore}%</Badge></td>
                                    <td><div className={`fw-bold assessment-${d.assessment.color}`}>{d.assessment.category}</div></td>
                                    <td className="fw-bold text-dark">{d.assessment.legacyCategory}</td>
                                    <td className="text-danger fw-bold">₹{d.penaltyAmount}</td>
                                    <td className="small text-start text-muted" style={{ maxWidth: '150px' }}>{d.assessment.observations.join(', ')}</td>
                                    <td className="small text-start text-success fw-bold" style={{ maxWidth: '150px' }}>{d.assessment.positives.join(', ')}</td>
                                    <td className="text-start" style={{ minWidth: '200px' }}>
                                        <div className="bg-light p-2 rounded-2 small border fw-bold text-dark">{d.assessment.actionPlan}</div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
            </Card>

            {selectedEmployeeIds.length === 1 && (
                <Card className="border-0 shadow-sm rounded-4 overflow-hidden border-top border-5 border-primary">
                    <Card.Header className="bg-white p-4 border-0 d-flex justify-content-between align-items-center">
                        <h5 className="fw-bold mb-0">Daily Discipline Audit Log</h5>
                        <Button variant="outline-primary" size="sm" className="rounded-pill px-3 fw-bold" onClick={() => handleDownloadIndividualLog(selectedEmployeeIds[0])}>
                            Download Individual Log
                        </Button>
                    </Card.Header>
                    <div className="table-responsive">
                        <Table striped hover className="align-middle mb-0 text-center">
                            <thead className="bg-light text-muted text-uppercase small">
                                <tr>
                                    <th className="py-3">Date</th>
                                    <th>In Time</th>
                                    <th>Out Time</th>
                                    <th>Status</th>
                                    <th>Daily Score</th>
                                    <th>SDS Impact</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(() => {
                                    const empId = selectedEmployeeIds[0];
                                    const empIdNorm = normalizeId(empId);
                                    const start = startDate ? moment(startDate) : moment([year, month - 1]).startOf('month');
                                    const end = endDate ? moment(endDate) : moment([year, month - 1]).endOf('month');
                                    let rows = [];
                                    let cur = start.clone();
                                    while (cur.isSameOrBefore(end)) {
                                        const dStr = cur.format('YYYY-MM-DD');
                                        const dayR = records.find(r => {
                                            const rNorm = r.normalizedEmployeeId || normalizeId(r.empCode || r.employeeId || r.uid);
                                            return rNorm === empIdNorm && moment(r.dateString || r.date?.toDate?.()).format('YYYY-MM-DD') === dStr;
                                        });
                                        const isOff = cur.day() === 2;
                                        const score = getDailyScore(dayR?.inTime, dayR?.outTime, isOff ? 'Weekly Off' : (dayR?.status || 'Absent'));

                                        // Detailed Status Logic
                                        let statusBadge = <Badge bg="danger">ABSENT</Badge>;
                                        if (isOff) {
                                            statusBadge = <Badge bg="secondary">WEEKLY OFF</Badge>;
                                        } else if (dayR && dayR.inTime && dayR.inTime !== '-') {
                                            const mins = parseTimeToMins(dayR.inTime);
                                            if (mins !== null) {
                                                if (mins <= 9 * 60 + 30) statusBadge = <Badge bg="success">EXCELLENT</Badge>;
                                                else if (mins <= 9 * 60 + 35) statusBadge = <Badge bg="primary">ON TIME</Badge>;
                                                else if (mins <= 9 * 60 + 40) statusBadge = <Badge bg="warning" text="dark">LATE</Badge>;
                                                else statusBadge = <Badge bg="danger">SUPER LATE</Badge>;
                                            }
                                        }

                                        rows.push(
                                            <tr key={dStr}>
                                                <td className="fw-bold text-muted">{cur.format('DD MMM (ddd)')}</td>
                                                <td className="fw-bold">{dayR?.inTime || '-'}</td>
                                                <td>{dayR?.outTime || '-'}</td>
                                                <td>{statusBadge}</td>
                                                <td className="fw-bold" style={{ fontSize: '1.1rem' }}>{score.isOff ? '-' : `${score.score}/10`}</td>
                                                <td>
                                                    {!score.isOff && (
                                                        <Badge bg={score.score >= 8 ? 'success' : (score.score >= 5 ? 'warning' : 'danger')} className="rounded-pill px-3 py-2 shadow-sm">
                                                            {score.score >= 0 ? `+${score.score} POINTS` : '0 POINTS'}
                                                        </Badge>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                        cur.add(1, 'days');
                                    }
                                    return rows;
                                })()}
                            </tbody>
                            <tfoot className="bg-light border-top border-2">
                                {(() => {
                                    const empData = analyticsData.find(d => String(d.id) === selectedEmployeeIds[0]);
                                    if (!empData) return null;
                                    return (
                                        <tr>
                                            <td className="text-end fw-bold py-3 fs-6">TOTALS / AVERAGES:</td>
                                            <td className="py-3">
                                                <div className="fw-bold text-dark">Avg: {empData.avgInTime}</div>
                                                <div className="small text-primary fw-bold">Real: {empData.realisticAvg}</div>
                                            </td>
                                            <td className="fw-bold py-3 text-dark">
                                                Avg OT: {empData.avgOTTime}
                                            </td>
                                            <td className="py-3"></td>
                                            <td className="text-center py-3">
                                                <div className="fw-bold fs-5 text-dark">{empData.earnedPoints}<small className="text-muted">/{empData.maxPoints}</small></div>
                                            </td>
                                            <td className="text-center py-3">
                                                <Badge bg={empData.sdsScore >= 80 ? 'success' : (empData.sdsScore >= 60 ? 'warning' : 'danger')} className="fs-6 py-2 px-4 rounded-pill">
                                                    {empData.sdsScore}% PERIOD SCORE
                                                </Badge>
                                            </td>
                                        </tr>
                                    );
                                })()}
                            </tfoot>
                        </Table>
                    </div>
                </Card>
            )}

            <style>{`
                .assessment-performer { color: #198754; }
                .assessment-inconsistent { color: #ffb100; }
                .assessment-risk { color: #fd7e14; }
                .assessment-critical { color: #dc3545; }
                .clickable-row { cursor: pointer; transition: all 0.2s; }
                .clickable-row:hover { background-color: #f8f9fa !important; }
                
                /* Premium Scrollbar */
                .table-responsive::-webkit-scrollbar {
                    height: 8px;
                }
                .table-responsive::-webkit-scrollbar-track {
                    background: #f1f1f1;
                    border-radius: 10px;
                }
                .table-responsive::-webkit-scrollbar-thumb {
                    background: #ccc;
                    border-radius: 10px;
                }
                .table-responsive::-webkit-scrollbar-thumb:hover {
                    background: #888;
                }
            `}</style>
        </div>
    );
};

export default DSADSAnalytics;
